import { RevCard, RevSpinner } from "@revmatics/catalyst";
import { PlatformContainer } from "./components/platformContainer/PlatformContainer.tsx";
import {
  useAppDispatch,
  useAppSelector,
} from "../../../../../stores/hooks/hooks.ts";
import { useEffect, useMemo } from "react";
import { supportedPlatforms } from "./data.ts";
import {
  removePlatformWithUsernames,
  removePlatformWithPartnerUsernames,
  selectPlatformUsername,
  selectPlatformPartnerUsername,
  addUsernameSection,
  addPartnerUsernameSection,
  removeUsernameSection,
  removePartnerUsernameSection,
  updateUsernameSection,
  updatePartnerUsernameSection,
} from "../../../../../stores/slice/brandSocialHandlesSlice.tsx";
import { fetchPlatforms } from "../../../../../stores/thunks/profileThunk.tsx";
import {
  fetchCompetitors,
  fetchCreatorPartnerUsernames,
  fetchSocialUsernames,
} from "../../../../../stores/thunks/brandSocialHandlesThunk.tsx";
import { useParams } from "react-router";
import { TitleWrapper } from "./components/titleWrapper/TitleWrapper.tsx";
import { CompetitorContainer } from "./components/competitorContainer/CompetitorContainer.tsx";
import { UsernameItem } from "../../../../../stores/types/brandSocialHandlesTypes.tsx";

export const SocialHandlesView = () => {
  const dispatch = useAppDispatch();
  const { brandId } = useParams();
  const { platforms, isLoadingPlatforms } = useAppSelector(
    (state) => state.profile,
  );
  const {
    isLoadingSocialUsernames,
    isLoadingCompetitors,
    isLoadingCreatorPartnerUsernames,
    usernames,
    partnerUsernames,
    usernamesOrder,
    partnerUsernamesOrder,
  } = useAppSelector((state) => state.brandSocialHandles);

  const isLoading =
    isLoadingSocialUsernames ||
    isLoadingCompetitors ||
    isLoadingCreatorPartnerUsernames ||
    isLoadingPlatforms;

  useEffect(() => {
    if (!platforms) {
      dispatch(fetchPlatforms());
    }
  }, [dispatch, platforms]);

  useEffect(() => {
    dispatch(fetchSocialUsernames(Number(brandId)));
    dispatch(fetchCompetitors(Number(brandId)));
    dispatch(fetchCreatorPartnerUsernames(Number(brandId)));
  }, [brandId, dispatch]);

  const filteredPlatforms = useMemo(() => {
    const supportedSet = new Set(supportedPlatforms);
    if (platforms) {
      return platforms.filter((platform) => supportedSet.has(platform.name));
    }
  }, [platforms]);

  if (isLoading) {
    return (
      <div className="h-full">
        <RevSpinner size="large" color="primary" centered />
      </div>
    );
  }

  if (filteredPlatforms && usernames && partnerUsernames) {
    return (
      <RevCard
        title="Social Media Usernames"
        text="Add usernames for your brand, key competitors, and creator partners."
        className="grid grid-cols-1 gap-10"
        paddingTitle="py-4 px-6"
      >
        <TitleWrapper
          title="Your Usernames"
          tooltip="Enter your social media handles to improve audience intelligence and persona discovery."
        >
          <PlatformContainer
            platforms={filteredPlatforms}
            usernames={usernames}
            selectPlatform={(platformId: number, oldPlatformId?: number) =>
              dispatch(selectPlatformUsername({ oldPlatformId, platformId }))
            }
            removePlatform={(platformId: number) =>
              dispatch(removePlatformWithUsernames({ platformId }))
            }
            addUsernameSection={(platformId: number) =>
              dispatch(addUsernameSection({ platformId }))
            }
            removeUsername={(platformId: number, usernameId: number) =>
              dispatch(removeUsernameSection({ platformId, usernameId }))
            }
            updateUsernameSection={(platformId: number, data: UsernameItem) =>
              dispatch(updateUsernameSection({ platformId, data }))
            }
            orders={usernamesOrder}
          />
        </TitleWrapper>
        <TitleWrapper
          title="Competitors"
          tooltip="Enter competitor websites and social handles to identify industry trends and help uncover untapped market opportunities."
        >
          <CompetitorContainer platforms={filteredPlatforms} />
        </TitleWrapper>

        <TitleWrapper
          title="Creator Partner Usernames"
          tooltip="Enter handles of creators you’ve previously worked with or admire to improve recommendation accuracy."
        >
          <PlatformContainer
            platforms={filteredPlatforms}
            usernames={partnerUsernames}
            selectPlatform={(platformId: number, oldPlatformId?: number) =>
              dispatch(
                selectPlatformPartnerUsername({ oldPlatformId, platformId }),
              )
            }
            removePlatform={(platformId: number) =>
              dispatch(removePlatformWithPartnerUsernames({ platformId }))
            }
            addUsernameSection={(platformId: number) =>
              dispatch(addPartnerUsernameSection({ platformId }))
            }
            removeUsername={(platformId: number, usernameId: number) =>
              dispatch(removePartnerUsernameSection({ platformId, usernameId }))
            }
            updateUsernameSection={(platformId: number, data: UsernameItem) =>
              dispatch(updatePartnerUsernameSection({ platformId, data }))
            }
            orders={partnerUsernamesOrder}
          />
        </TitleWrapper>
      </RevCard>
    );
  }
  return null;
};
